import { useCallback, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAutofillSync } from '@/lib/useAutofillSync';
import { Button } from '@/components/ui/Button';
import { Field, PASSWORD_MIN, PasswordField } from '@/components/ui/Field';
import { ErrorBlock, useCountdown } from '@/components/ui/Feedback';
import type { ApiError } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/AuthContext';
import { routes } from '@/routes/paths';
import { AuthLayout, AuthSwap, authStyles as s } from './AuthLayout';
import { useAuthSubmit } from './useAuthSubmit';
import { t } from '@/lib/i18n';

const NAME_MAX = 80;

const mapRegisterError = (err: ApiError) => {
  if (err.isConflict) return { field: 'email', message: t('auth.register.emailTaken') };
  // class-validator returns every failure in one joined string; route it to the field it names.
  if (err.status === 400 && /email/i.test(err.message)) {
    return { field: 'email', message: t('auth.register.emailInvalid') };
  }
  if (err.status === 400 && /password/i.test(err.message)) {
    return { field: 'password', message: t('auth.passwordMin', { min: PASSWORD_MIN }) };
  }
  return null;
};

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const { busy, fieldErrors, formError, rateLimitedFor, submit, clearRateLimit } =
    useAuthSubmit(mapRegisterError);

  const secondsLeft = useCountdown(rateLimitedFor, clearRateLimit);

  // The submit button here is gated on validity by design, so an autofill React never
  // saw would leave it permanently dead. Sync it.
  const onAutofill = useCallback((values: Record<string, string>) => {
    if (values.displayName) setDisplayName((v) => v || (values.displayName as string));
    if (values.email) setEmail((v) => v || (values.email as string));
    if (values.password) setPassword((v) => v || (values.password as string));
  }, []);
  useAutofillSync(formRef, onAutofill);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void submit(async () => {
        await register({ email: email.trim(), password, displayName: displayName.trim() });
        navigate(routes.home, { replace: true });
      });
    },
    [submit, register, email, password, displayName, navigate],
  );

  const blocked = secondsLeft != null && secondsLeft > 0;

  /**
   * The whole reason this screen validates before submitting: the watch app accepts a
   * 6-character password and the server requires 8. A user who set one on the watch, or who
   * simply types six characters, gets a rejected sign-up with no explanation. Blocking the
   * button until the rule is met turns the most common failure into a non-event.
   */
  const nameOk = displayName.trim().length >= 1 && displayName.trim().length <= NAME_MAX;
  const emailOk = /.+@.+\..+/.test(email.trim());
  const passwordOk = password.length >= PASSWORD_MIN && password.length <= 128;
  const canSubmit = nameOk && emailOk && passwordOk && !blocked;

  return (
    <AuthLayout
      title={t('auth.register.title')}
      subtitle={t('auth.sameAccount')}
      hero="compact"
      backTo={routes.login}
    >
      <form onSubmit={onSubmit} noValidate ref={formRef}>
        {(blocked || formError) && (
          <div className={s.topSlot}>
            {blocked ? (
              <ErrorBlock title={t('auth.tooManyAttempts')} secondsLeft={secondsLeft ?? 0}>
                {t('auth.waitAMinute')}
              </ErrorBlock>
            ) : (
              <ErrorBlock title={t('auth.register.failed')}>{formError}</ErrorBlock>
            )}
          </div>
        )}

        <Field
          label={t('auth.field.displayName')}
          name="displayName"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={NAME_MAX}
          hint={`1 a ${NAME_MAX} caracteres`}
          showCounter
          error={fieldErrors.displayName ?? null}
          required
        />

        <Field
          label={t('auth.field.email')}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email ?? null}
          required
        />

        <PasswordField
          label={t('auth.field.password')}
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          showStrength
          hint={t('auth.passwordMin', { min: PASSWORD_MIN })}
          error={fieldErrors.password ?? null}
          required
        />

        <div className={s.actions}>
          <Button type="submit" block size="lg" busy={busy} disabled={!canSubmit}>
            {t('auth.register.submit')}
          </Button>
        </div>

        {/*
          A razão da regra dos 8 (o relógio aceita 6, o servidor exige 8) é anotação de design e
          saiu da tela. A regra em si continua visível onde importa: no contador do campo de
          senha, antes do envio.
        */}
        <AuthSwap
          question={t('auth.register.hasAccount')}
          linkLabel={t('auth.login.title')}
          to={routes.login}
        />
      </form>
    </AuthLayout>
  );
}
